import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../context/AuthContext';
import { Star, Heart, MapPin, Truck, Store, ChevronLeft, ChevronRight, MessageCircle, Share2, X, ZoomIn, Flag } from 'lucide-react';
import FeaturedProducts from '../components/FeaturedProducts';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const genCaptcha = () => {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { question: `${a} + ${b}`, answer: a + b };
};

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [review, setReview] = useState({ rating: 5, comment: '', reviewer_email: '' });
  const [hasReviewed, setHasReviewed] = useState(false);
  const [captcha, setCaptcha] = useState(() => genCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');
  const [inWishlist, setInWishlist] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');

  const share = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: product?.title, url });
    } else {
      navigator.clipboard.writeText(url);
      alert('¡Enlace copiado al portapapeles!');
    }
  };

  const shareWhatsApp = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`¡Mira este producto en MotoMarket! ${product?.title} - ₡${product?.price?.toLocaleString('es-CR')}`);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
  };

  const contactWhatsApp = () => {
    const phone = product?.seller?.phone?.replace(/\D/g, '');
    if (!phone) return alert('El vendedor no tiene WhatsApp registrado');
    const text = encodeURIComponent(`Hola, vi tu producto en MotoMarket: ${product.title} (₡${product.price?.toLocaleString('es-CR')})`);
    window.open(`https://wa.me/506${phone}?text=${text}`, '_blank');
  };

  const submitReport = async () => {
    if (!reportReason) return;
    try {
      await api.post('/reports', { product_id: id, reason: reportReason });
      setShowReport(false);
      setReportReason('');
      alert('Reporte enviado. Lo revisaremos pronto.');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al enviar reporte');
    }
  };

  useEffect(() => { 
    fetchProduct(); 
    // Track view
    if (id) {
      fetch('/api/track/view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: id })
      }).catch(() => {});
      // Track engagement event if logged in
      if (user?.id) {
        fetch('/api/track/event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
          body: JSON.stringify({ product_id: id, event_type: 'view' })
        }).catch(() => {});
      }
    }
  }, [id, user]);

  const fetchProduct = async () => {
    try {
      const { data } = await api.get(`/products/${id}`);
      setProduct(data);
      if (user && data.reviews) setHasReviewed(data.reviews.some(r => r.buyer_id === user.id));
      // Check wishlist
      if (user) {
        try {
          const wl = await api.get('/wishlist');
          setInWishlist((wl.data || []).some(p => p.id === data.id));
        } catch {}
      }
    } catch { navigate('/products'); }
    finally { setLoading(false); }
  };

  const toggleWishlist = async () => {
    if (!user) return navigate('/login');
    try {
      if (inWishlist) { 
        await api.delete(`/wishlist/${id}`); 
        setInWishlist(false);
      }
      else { 
        await api.post('/wishlist', { product_id: id }); 
        setInWishlist(true);
        // Track wishlist add
        fetch('/api/track/event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
          body: JSON.stringify({ product_id: id, event_type: 'wishlist' })
        }).catch(() => {});
      }
    } catch {}
  };

  const sendMessage = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    const message = `Hola, me interesa tu producto: ${product.title}`;
    
    try {
      const { data } = await api.post('/products/contact', {
        product_id: product.id,
        message: message
      });
      
      if (data && data.conversation_id) {
        // Track contact event
        fetch('/api/track/event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
          body: JSON.stringify({ product_id: id, seller_id: product.seller_id, event_type: 'contact' })
        }).catch(() => {});
        // Navigate to the product chat
        navigate(`/wants-chat/${data.conversation_id}`);
      }
    } catch (err) {
      console.error('Error:', err);
      alert(err.response?.data?.error || 'Error al enviar mensaje');
    }
  };

  const submitReview = async () => {
    if (!user) {
      alert('Debes iniciar sesión para dejar una reseña.');
      navigate('/login');
      return;
    }
    if (!review.reviewer_email?.trim()) { alert('El email es requerido.'); return; }
    if (parseInt(captchaInput) !== captcha.answer) {
      alert('Captcha incorrecto. Intenta de nuevo.');
      setCaptcha(genCaptcha());
      setCaptchaInput('');
      return;
    }
    console.log('Submitting review:', { product_id: id, ...review });
    try {
      const result = await api.post('/reviews', { product_id: id, ...review });
      console.log('Review submitted:', result);
      setHasReviewed(true);
      alert('Reseña enviada. Será visible una vez aprobada por el administrador.');
      fetchProduct();
      setReview({ rating: 5, comment: '', reviewer_email: '' });
      setCaptchaInput('');
      setCaptcha(genCaptcha());
    } catch (err) {
      console.error('Review error:', err);
      const errorMsg = err?.response?.data?.error || 'Error al enviar la reseña';
      alert(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-96 bg-gray-200 rounded-xl" />
          <div className="h-8 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (!product) return null;

  const images = product.images?.length > 0 ? product.images : [{ url: 'https://via.placeholder.com/600x400?text=Sin+imagen' }];
  const avgRating = product.avg_rating || 0;

  return (
    <div className="animate-fade-in">
      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setLightbox(false)}>
          <button className="absolute top-4 right-4 text-white w-10 h-10 flex items-center justify-center hover:bg-white/20 rounded-full">
            <X className="w-6 h-6" />
          </button>
          {images.length > 1 && (
            <button onClick={e => { e.stopPropagation(); setSelectedImage(i => Math.max(0, i - 1)); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/40">
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          <img
            src={images[selectedImage]?.url}
            alt={product.title}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={e => e.stopPropagation()}
          />
          {images.length > 1 && (
            <button onClick={e => { e.stopPropagation(); setSelectedImage(i => Math.min(images.length - 1, i + 1)); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/40">
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
          <div className="absolute bottom-4 text-white text-sm">{selectedImage + 1} / {images.length}</div>
        </div>
      )}
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <nav className="text-sm text-gray-500 flex gap-2">
          <Link to="/">Inicio</Link>
          <span>/</span>
          <Link to="/products">Productos</Link>
          <span>/</span>
          <span className="text-gray-900">{product.title}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Gallery */}
          <div>
            <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden mb-4">
              <img 
                src={images[selectedImage]?.url || images[0]}
                alt={product.title}
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => setLightbox(true)}
              />
              <button
                onClick={() => setLightbox(true)}
                className="absolute bottom-3 right-3 w-9 h-9 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              {images.length > 1 && (
                <>
                  <button 
                    onClick={() => setSelectedImage(i => Math.max(0, i - 1))}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => setSelectedImage(i => Math.min(images.length - 1, i + 1))}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnails */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${selectedImage === i ? 'border-primary' : 'border-transparent'}`}
                >
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div>
            {/* Title & Brand */}
            {product.category && (
              <span className="text-sm text-gray-500">{product.category.brand} {product.category.model}</span>
            )}
            <h1 className="text-2xl md:text-3xl font-bold mt-1 mb-4">{product.title}</h1>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} className={`w-5 h-5 ${i <= Math.round(avgRating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                ))}
              </div>
              <span className="text-sm text-gray-500">
                {avgRating.toFixed(1)} ({product.review_count || 0} reseñas) • {product.views || 0} vistas
              </span>
            </div>

            {/* Price */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold text-primary">
                  ₡{product.price.toLocaleString('es-CR')}
                </span>
                {product.negotiable && (
                  <span className="text-sm text-gray-500">Negociable</span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mb-4">
              {user ? (
                <button 
                  onClick={sendMessage}
                  className="flex-1 flex items-center justify-center gap-2 bg-yellow-400 border-2 border-black text-black py-3 rounded-full font-semibold hover:bg-yellow-500 transition"
                >
                  <MessageCircle className="w-5 h-5" />
                  Contactar
                </button>
              ) : null}
              <button onClick={toggleWishlist} className="w-12 h-12 flex items-center justify-center border-2 rounded-full hover:border-primary hover:text-primary transition">
                <Heart className={`w-5 h-5 transition-colors ${inWishlist ? 'fill-primary text-primary' : ''}`} />
              </button>
              <button onClick={share} className="w-12 h-12 flex items-center justify-center border-2 rounded-full hover:border-primary hover:text-primary transition" title="Compartir">
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* WhatsApp buttons */}
            {user ? (
              <div className="flex gap-3 mb-6">
                <button
                  onClick={contactWhatsApp}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-full font-semibold hover:bg-[#1ebe5d] transition"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp al Vendedor
                </button>
                <button
                  onClick={shareWhatsApp}
                  title="Compartir por WhatsApp"
                  className="w-12 h-12 flex items-center justify-center bg-[#25D366] text-white rounded-full hover:bg-[#1ebe5d] transition"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="mb-6 bg-yellow-50 border border-yellow-300 rounded-xl p-4 text-sm text-yellow-800">
                <p className="font-semibold mb-1">¿Querés contactar al vendedor?</p>
                <p className="mb-3">Necesitás una cuenta para ver los datos de contacto y enviar mensajes.</p>
                <div className="flex gap-2">
                  <Link to="/register" className="flex-1 text-center bg-yellow-400 border-2 border-black text-black py-2 rounded-full font-semibold hover:bg-yellow-500 transition">
                    Crear cuenta
                  </Link>
                  <Link to="/login" className="flex-1 text-center border-2 border-gray-300 text-gray-700 py-2 rounded-full font-semibold hover:border-gray-500 transition">
                    Iniciar sesión
                  </Link>
                </div>
              </div>
            )}

            {/* Seller Card */}
            <div className="bg-white border rounded-xl p-4 mb-2">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-xl font-bold">
                  {product.seller?.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{product.seller?.name}</p>
                  <div className="flex items-center gap-2 text-sm">
                    {product.seller?.is_verified && (
                      <span className="badge-verified">✓ Verificado</span>
                    )}
                    <span className="text-gray-500">
                      {product.province?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Report link */}
            {user && user.id !== product.seller?.id && (
              <div className="flex justify-end mb-6">
                <button onClick={() => setShowReport(true)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition">
                  <Flag className="w-3 h-3" /> Reportar anuncio
                </button>
              </div>
            )}

            {/* Report Modal */}
            {showReport && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowReport(false)}>
                <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                  <h3 className="font-bold text-lg mb-4">Reportar anuncio</h3>
                  <div className="space-y-2 mb-4">
                    {['Producto falso o engañoso', 'Precio abusivo', 'Contenido inapropiado', 'Vendedor sospechoso', 'Otro'].map(r => (
                      <label key={r} className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-gray-50">
                        <input type="radio" name="reason" value={r} checked={reportReason === r} onChange={() => setReportReason(r)} />
                        <span className="text-sm">{r}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setShowReport(false)} className="flex-1 py-2 border rounded-full text-sm">Cancelar</button>
                    <button onClick={submitReport} disabled={!reportReason} className="flex-1 py-2 bg-red-500 text-white rounded-full text-sm font-semibold disabled:opacity-50">Enviar Reporte</button>
                  </div>
                </div>
              </div>
            )}

            {/* Shop Location Map */}
            {product.show_location && product.seller?.shop_lat && product.seller?.shop_lng && (
              <div className="bg-white border rounded-xl p-4 mb-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> Ubicación de la tienda</h3>
                {product.seller.shop_address && (
                  <p className="text-sm text-gray-600 mb-3">📍 {product.seller.shop_address}</p>
                )}
                <div className="rounded-lg overflow-hidden" style={{height: '220px'}}>
                  <MapContainer
                    center={{ lat: product.seller.shop_lat, lng: product.seller.shop_lng }}
                    zoom={15}
                    style={{height: '100%', width: '100%'}}
                    scrollWheelZoom={false}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                    <Marker position={{ lat: product.seller.shop_lat, lng: product.seller.shop_lng }}>
                      <Popup>{product.seller.name}{product.seller.shop_address ? ` · ${product.seller.shop_address}` : ''}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>
            )}

            {/* Details */}
            <div className="space-y-4">
              <h3 className="font-semibold">Detalles</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Condición</span>
                  <p className="font-medium">{product.condition === 'NEW' ? 'Nuevo' : 'Usado'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Ubicación</span>
                  <p className="font-medium">{product.province?.replace('_', ' ')}</p>
                </div>
                <div>
                  <span className="text-gray-500">Entrega</span>
                  <p className="font-medium">
                    {[
                      product.delivery_options?.includes('pub') && 'Recoger en persona',
                      product.delivery_options?.includes('dropoff') && 'A domicilio'
                    ].filter(Boolean).join(' · ') || 'Recoger en persona'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {product.description && (
          <div className="mt-8">
            <h3 className="text-xl font-bold mb-4">Descripción</h3>
            <div className="bg-white border rounded-xl p-6">
              <p className="whitespace-pre-wrap">{product.description}</p>
            </div>
          </div>
        )}

        {/* Featured Products */}
        <div className="mt-8">
          <h3 className="text-xl font-bold mb-4">Productos Destacados</h3>
          <FeaturedProducts currentProductId={product.id} />
        </div>

        {/* Reviews */}
        <div className="mt-8">
          <h3 className="text-xl font-bold mb-4">Reseñas ({product.reviews?.length || 0})</h3>
          
          {product.reviews?.length > 0 ? (
            <div className="space-y-4">
              {product.reviews.map(review => (
                <div key={review.id} className="bg-white border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-bold">
                        {review.buyer?.name?.charAt(0) || '?'}
                      </div>
                      <span className="font-medium">{review.buyer?.name}</span>
                    </div>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Star key={i} className={`w-4 h-4 ${i <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                      ))}
                    </div>
                  </div>
                  {review.title && <p className="font-medium mb-1">{review.title}</p>}
                  <p className="text-gray-600">{review.comment}</p>
                  <p className="text-xs text-gray-400 mt-2">
                    {new Date(review.created_at).toLocaleDateString('es-CR')}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Aún no hay reseñas</p>
          )}

          {/* Write Review - Only show if user hasn't reviewed */}
          {user && !hasReviewed && (
            <div className="mt-6 bg-white border rounded-xl p-6">
              <h4 className="font-semibold mb-4">Sé honesto con tu calificación, tu nombre aparecerá junto a tu calificación</h4>
              <div className="flex items-center gap-2 mb-4">
                <span>Calificación:</span>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map(i => (
                    <button key={i} onClick={() => setReview({ ...review, rating: i })}>
                      <Star className={`w-6 h-6 ${i <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                value={review.comment}
                onChange={(e) => setReview({ ...review, comment: e.target.value })}
                placeholder="Cuéntanos tu experiencia..."
                className="w-full border rounded-lg p-3 mb-4"
                rows="3"
              />
              <input
                type="email"
                value={review.reviewer_email}
                onChange={(e) => setReview({ ...review, reviewer_email: e.target.value })}
                placeholder="Tu email (requerido, no se publicará)"
                className="w-full border rounded-lg p-3 mb-4"
                required
              />
              <div className="flex items-center gap-3 mb-4">
                <span className="text-sm text-gray-600">¿Cuánto es <strong>{captcha.question}</strong>?</span>
                <input
                  type="number"
                  value={captchaInput}
                  onChange={e => setCaptchaInput(e.target.value)}
                  className="w-20 border rounded-lg px-3 py-1.5 text-sm"
                  placeholder="?"
                />
              </div>
              <button onClick={submitReview} disabled={!captchaInput || !review.reviewer_email?.trim()} className="bg-primary text-white px-6 py-2 rounded-full disabled:opacity-50">
                Enviar Reseña
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}